<!-- eslint-disable vue/no-mutating-props -->
<script>
import { isPhoneNumberValid } from 'shared/helpers/Validators';
import parsePhoneNumber from 'libphonenumber-js';
import InboxDropdownItem from 'dashboard/components/widgets/InboxDropdownItem.vue';
import { getInboxIdentifier } from 'dashboard/helper/inbox';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { mapGetters } from 'vuex';
import { frontendURL } from 'dashboard/helper/URLHelper';

export default {
  components: { InboxDropdownItem },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    inboxesList: {
      type: Array,
      default: () => [],
    },
    currentInbox: {
      type: Number,
      default: 0,
    },
  },
  emits: ['onClose'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isLoading: false,
      targetInbox: null,
      phoneNumber: '',
      phoneNumberNotChanged: true,
      activeDialCode: '',
      contactName: '',
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
    }),
    newInteractionButtonDisabled() {
      const badPhone = this.isPhoneNumberNotValid || !this.phoneNumber;
      return this.v$.$invalid || badPhone;
    },
    isPhoneNumberNotValid() {
      if (this.phoneNumber !== '') {
        return (
          !isPhoneNumberValid(this.phoneNumber, this.activeDialCode) ||
          (this.phoneNumber !== '' ? this.activeDialCode === '' : false)
        );
      }
      return false;
    },
    phoneNumberError() {
      if (this.activeDialCode === '') {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DIAL_CODE_ERROR');
      }
      if (!isPhoneNumberValid(this.phoneNumber, this.activeDialCode)) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.ERROR');
      }
      return '';
    },
    parsePhoneNumber() {
      return parsePhoneNumber(this.phoneNumber);
    },
    inboxes() {
      return this.inboxesList
        .filter(
          inbox =>
            inbox.channel_type === 'Channel::Whatsapp' ||
            inbox.channel_type === 'Channel::CommonWhatsapp'
        )
        .map(inbox => ({
          ...inbox,
          sourceId: inbox.id,
        }));
    },
    setPhoneNumber() {
      if (this.parsePhoneNumber && this.parsePhoneNumber.countryCallingCode) {
        return this.phoneNumber;
      }
      if (this.phoneNumber === '' && this.activeDialCode !== '') {
        return '';
      }
      return this.activeDialCode
        ? `${this.activeDialCode}${this.phoneNumber}`
        : '';
    },
  },
  validations() {
    const validations = {
      phoneNumber: {},
      targetInbox: {},
    };

    return validations;
  },
  watch: {
    phoneNumber() {
      if (this.phoneNumberNotChanged) this.phoneNumberNotChanged = false;
    },
  },
  mounted() {},
  methods: {
    onClose() {
      this.$emit('onClose');
    },
    computedInboxSource(inbox) {
      return getInboxIdentifier(inbox);
    },
    setPhoneCode(code) {
      if (this.phoneNumber !== '' && this.parsePhoneNumber) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;
        if (dialCode === code) {
          return;
        }
        this.activeDialCode = `+${dialCode}`;
        const newPhoneNumber = this.phoneNumber.replace(
          `+${dialCode}`,
          `${code}`
        );
        this.phoneNumber = newPhoneNumber;
      } else {
        this.activeDialCode = code;
      }
    },
    canNotRequestWithCurrentParams() {
      const problemInPhoneNumber =
        !this.phoneNumberNotChanged &&
        (this.isPhoneNumberNotValid || !this.phoneNumber);

      return !this.targetInbox || problemInPhoneNumber;
    },
    async createNewInteraction() {
      if (this.canNotRequestWithCurrentParams()) {
        useAlert(this.$t('QUICK_MESSAGE.PARAMS_ERROR'));
        return;
      }

      const payload = {
        inbox_id: this.targetInbox.id,
        assignee_id: this.currentUser.id,
        contact_attributes: { phone_number: this.setPhoneNumber },
      };
      if (this.contactName) payload.contact_attributes.name = this.contactName;
      try {
        this.isLoading = true;
        const { data } = await this.$store.dispatch('newInteraction', payload);
        useAlert(this.$t('QUICK_MESSAGE.API.SUCCESS_MESSAGE'));
        this.isLoading = false;
        this.onClose();

        if (data?.link_to_conversation)
          this.redirectToConversationPage(data.link_to_conversation);
      } catch (err) {
        useAlert(this.$t('QUICK_MESSAGE.API.ERROR_MESSAGE'));
        this.isLoading = false;
      }
    },
    redirectToConversationPage(linkToConversation) {
      // Como o pusher adiciona automaticamente o caminho padrão do router, vou fazer a exclusão do FRONTEND URL que veio do back e deixar o router resolver isso
      // Dessa forma, só preciso usar o conversation ID que foi retornado
      // Removendo account_id da URL prefixada
      const conversationId = linkToConversation.split('/').pop();
      this.$router.push(
        frontendURL(
          `accounts/${this.accountId}/conversations/${conversationId}`
        )
      );
    },
  },
};
</script>

<template>
  <woot-modal
    size="tiny"
    :show="show"
    :on-close="onClose"
    @update:show="val => (show = val)"
  >
    <div class="column content-box">
      <woot-modal-header
        :header-title="$t('QUICK_MESSAGE.HEADER')"
        :header-content="$t('QUICK_MESSAGE.DESCRIPTION')"
        class="mb-4"
      />
      <div class="main-container">
        <!-- START SELECT INBOX SECTION -->
        <div class="w-[90%]">
          <label>
            {{ $t('NEW_CONVERSATION.FORM.INBOX.LABEL') }}
          </label>
          <div
            class="multiselect-wrap--small"
            :class="{ 'has-multi-select-error': v$.targetInbox.$error }"
          >
            <multiselect
              v-model="targetInbox"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              selected-label=""
              select-label=""
              deselect-label=""
              :max-height="160"
              close-on-select
              :options="[...inboxes]"
            >
              <template #singleLabel="{ option }">
                <InboxDropdownItem
                  v-if="option.name"
                  :name="option.name"
                  :inbox-identifier="computedInboxSource(option)"
                  :channel-type="option.channel_type"
                />
                <span v-else>
                  {{ $t('NEW_CONVERSATION.FORM.INBOX.PLACEHOLDER') }}
                </span>
              </template>
              <template #option="{ option }">
                <InboxDropdownItem
                  :name="option.name"
                  :inbox-identifier="computedInboxSource(option)"
                  :channel-type="option.channel_type"
                />
              </template>
            </multiselect>
          </div>
          <label :class="{ error: v$.targetInbox.$error }">
            <span v-if="v$.targetInbox.$error" class="message">
              {{ $t('NEW_CONVERSATION.FORM.INBOX.ERROR') }}
            </span>
          </label>
        </div>
        <!-- END SELECT INBOX SECTION -->

        <!-- START PHONE NUMBER SECTION -->
        <div class="w-[90%]">
          <label
            :class="{
              error: isPhoneNumberNotValid,
            }"
          >
            {{ $t('CONTACT_FORM.FORM.PHONE_NUMBER.LABEL') }}
            <woot-phone-input
              v-model="phoneNumber"
              :error="isPhoneNumberNotValid"
              :placeholder="$t('CONTACT_FORM.FORM.PHONE_NUMBER.PLACEHOLDER')"
              @blur="v$.phoneNumber.$touch"
              @set-code="setPhoneCode"
            />
            <span v-if="isPhoneNumberNotValid" class="message">
              {{ phoneNumberError }}
            </span>
          </label>
          <div
            v-if="
              !phoneNumberNotChanged && (isPhoneNumberNotValid || !phoneNumber)
            "
            class="relative mx-0 mt-0 mb-2.5 p-2 rounded-md text-xs border border-solid border-yellow-500 text-yellow-700 dark:border-yellow-700 bg-yellow-200/60 dark:bg-yellow-200/20 dark:text-yellow-400"
          >
            {{ $t('CONTACT_FORM.FORM.PHONE_NUMBER.HELP') }}
          </div>
        </div>
        <!-- END PHONE NUMBER SECTION -->

        <!-- START CONTACT NAME SECTION -->
        <div class="w-[90%]">
          <label>
            {{ $t('QUICK_MESSAGE.CONTACT_NAME') }}
            <input
              v-model.trim="contactName"
              type="text"
              :placeholder="$t('QUICK_MESSAGE.CONTACT_NAME_PLACEHOLDER')"
            />
          </label>
        </div>
        <!-- END CONTACT NAME SECTION -->

        <woot-button
          class-names="resolve"
          color-scheme="success"
          icon="checkmark"
          emoji="✅"
          :is-loading="isLoading"
          class="mb-8"
          :disabled="newInteractionButtonDisabled"
          @click="createNewInteraction"
        >
          {{ $t('QUICK_MESSAGE.BUTTON') }}
        </woot-button>
      </div>
    </div>
  </woot-modal>
</template>

<style lang="scss" scoped>
.main-container,
.mode-selection-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}
.mode-selection-container {
  align-items: flex-start;
}
</style>
